/**
 * 「TA 的星球生活」（UI-WORLD-LIVING-117-3，居民主页）要说的几件事：从公开事实里挑，纯函数，不碰 DOM。
 * 来源只有三处，都是后台给的公开事实，不编、不改写：
 * - 公开主页（PublicPetView）：此刻（resident.doing、asleep）、TA 自己发的公开小事（posts，TA 的口吻）、梦想；
 * - TA 的公开时间线（GET /public/residents/{pet_id}/timeline，B 的 PUBLIC-RESIDENT-TIMELINE-117）：出门、到了、回驿站，
 *   TA 是主角或同行的公开照片（photo_posted），TA 参与的相遇（met，对方也此刻公开）；每条和全星球动态里同一条逐字一样；
 * - 见过的那几位的名字、形象：对方自己的公开主页（页面去读，这里只给编号）。
 * 时刻按东八区：星球先国内（用户 10-08），现有城市都在东八区，公开层不带时区；不按看的人本机的时区。
 */
import type { PlanetEventTag, Post, PublicFeedItem, PublicFeedPhoto } from "@/shared/contracts";

/** 星球城市的时区（见文件头） */
export const PLANET_TIME_ZONE = "Asia/Shanghai";
/** 「TA 的一天」最多几条（不含第一条「此刻」）：参考稿 05-life-A 是 3–4 条 */
export const DAY_LIMIT = 3;
/** 「TA 最近见过的」最多几位：参考稿 2–3 位，不做头像墙 */
export const FRIEND_LIMIT = 3;

const dayFormat = new Intl.DateTimeFormat("en-CA", { timeZone: PLANET_TIME_ZONE, year: "numeric", month: "2-digit", day: "2-digit" });
const clockFormat = new Intl.DateTimeFormat("zh-CN", { timeZone: PLANET_TIME_ZONE, hour: "2-digit", minute: "2-digit", hour12: false });

/** 星球城市当地的日子（YYYY-MM-DD） */
export function planetDay(ms: number): string {
  return dayFormat.format(new Date(ms));
}

/** 星球城市当地的时刻（HH:MM） */
export function planetClock(ms: number): string {
  return clockFormat.format(new Date(ms));
}

/** 「10 月 14 日」：节标题旁说是哪一天（不是今天时才说） */
export function dayLabel(day: string): string {
  const [, month, date] = day.split("-").map(Number);
  return `${month} 月 ${date} 日`;
}

/** 一天里的一条：时刻、一句话（后台原文）、哪一类；照片条带那张真实照片；TA 自己的小事带它属于哪个星球活动（世界运营第二期 D9，有才带） */
export type DayEntry = {
  key: string;
  at: string;
  ms: number;
  text: string;
  kind: "post" | PublicFeedItem["kind"];
  photo: PublicFeedPhoto | null;
  event: PlanetEventTag | null;
};

/** 「TA 的一天」：挑出来的那一天（不是今天时给日子，节标题旁说），和按时刻正序的几条 */
export type LifeDay = { day: string | null; entries: DayEntry[] };

const RICH = new Set<DayEntry["kind"]>(["post", "photo_posted", "met"]);
const ROUTE = new Set<DayEntry["kind"]>(["outing_started", "event_joined", "arrived", "came_home"]);

function stamp(iso: string): number {
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : Number.NaN;
}

/**
 * 「TA 的一天」挑哪几条：
 * - 候选：TA 自己发的公开小事（TA 的口吻）、TA 的照片（只用有图的 photo_posted，真实照片）、TA 参与的相遇；
 * - 这几样一条都没有时，才用出门、到了、回驿站这几句（免得和 TA 自己说的那句「去咖啡馆转了转」重复说一遍）；
 * - 取今天的；今天没有就取最近有事的那一天（day 给日子）；取那一天最新的 DAY_LIMIT 条，再按时刻正序排；
 * - 时间线里不是 TA 的（pet_ids 不含 TA）不要；同一条（event_id／post_id）只要一次；时刻读不出来的不要。
 */
export function lifeDay({ posts, items, petId, nowMs }: { posts: readonly Post[]; items: readonly PublicFeedItem[]; petId: string; nowMs: number }): LifeDay {
  const seen = new Set<string>();
  const all: DayEntry[] = [];
  const add = (entry: DayEntry) => {
    if (!Number.isFinite(entry.ms) || seen.has(entry.key) || !entry.text.trim()) return;
    seen.add(entry.key);
    all.push(entry);
  };
  for (const post of posts) add({ key: `post:${post.post_id}`, at: post.created_at, ms: stamp(post.created_at), text: post.text.trim(), kind: "post", photo: null, event: post.planet_event ?? null });
  for (const item of items) {
    if (!item.pet_ids.includes(petId)) continue;
    if (item.kind === "photo_posted" && !item.photo) continue;
    add({ key: `event:${item.event_id}`, at: item.at, ms: stamp(item.at), text: item.text.trim(), kind: item.kind, photo: item.kind === "photo_posted" ? item.photo : null, event: null });
  }
  const rich = all.filter((entry) => RICH.has(entry.kind));
  const pool = rich.length ? rich : all.filter((entry) => ROUTE.has(entry.kind));
  if (!pool.length) return { day: null, entries: [] };
  const today = planetDay(nowMs);
  const days = [...new Set(pool.map((entry) => planetDay(entry.ms)))].filter((day) => day <= today).sort().reverse();
  const day = days[0];
  if (!day) return { day: null, entries: [] };
  const entries = pool
    .filter((entry) => planetDay(entry.ms) === day)
    .sort((a, b) => b.ms - a.ms || (a.key < b.key ? 1 : -1))
    .slice(0, DAY_LIMIT)
    .reverse();
  return { day: day === today ? null : day, entries };
}

/** TA 最近见过的一位：对方编号，和最近那次相遇的时刻（名字下写哪天见过） */
export type LifeFriend = { petId: string; at: string };

/**
 * 「TA 最近见过的」：时间线里 TA 参与的相遇（met）的对方。08a9 的读口要求两边都公开、两个方向的认识关系（共同经历留下的边）都还在、
 * 互不屏蔽、都没被藏起——保证的是「互相认识」，不是结交，所以不叫朋友（I 10-16）。
 * 按最近一次相遇排，一位只出现一次，最多 FRIEND_LIMIT 位；名字下写哪天见过（metDay；相遇那句原文已在 TA 的一天里，不再说一遍）。
 */
export function lifeFriends(items: readonly PublicFeedItem[], petId: string): LifeFriend[] {
  const met = items.filter((item) => item.kind === "met" && item.pet_ids.includes(petId) && Number.isFinite(stamp(item.at)))
    .sort((a, b) => stamp(b.at) - stamp(a.at));
  const friends: LifeFriend[] = [];
  for (const item of met) {
    for (const other of item.pet_ids) {
      if (other === petId || friends.some((friend) => friend.petId === other)) continue;
      friends.push({ petId: other, at: item.at });
    }
  }
  return friends.slice(0, FRIEND_LIMIT);
}

/**
 * 见过的那位名字下写哪天：今天／10 月 15 日（东八区；读不出时刻就不写）。不带「见过」：节标题已经说了，
 * 320 宽下「10 月 15 日见过」要折出一个「过」（评审 R2 阻断）。
 */
export function metDay(at: string, nowMs: number): string {
  const ms = stamp(at);
  if (!Number.isFinite(ms)) return "";
  const day = planetDay(ms);
  return day === planetDay(nowMs) ? "今天" : dayLabel(day);
}

/** 「TA 的照片」：TA 是主角或同行的公开照片，新的在前；同一张图只要一次 */
export function lifePhotos(items: readonly PublicFeedItem[], petId: string): PublicFeedPhoto[] {
  const photos: PublicFeedPhoto[] = [];
  for (const item of [...items].sort((a, b) => stamp(b.at) - stamp(a.at))) {
    if (item.kind !== "photo_posted" || !item.photo || !item.pet_ids.includes(petId)) continue;
    if (photos.some((photo) => photo.url === item.photo!.url)) continue;
    photos.push(item.photo);
  }
  return photos;
}
